import React from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { FormSection } from '@/shared/ui/components/form/FormSectionCollapsible';
import { Label, Text, TextInput } from '@/shared/ui';
import { Select } from '@/shared/ui/components/form/inputs/Select';
import { maskCurrency } from '../../utils/masks';
import { FieldError } from './FieldError';
import { labelStyle, currencyInputPadding, currencyPrefixStyle } from './drawer-styles';
import type { ClientFormData } from './schema';

interface FinancialSectionProps {
  form: UseFormReturn<ClientFormData>;
  collapsed: boolean;
  onToggle: () => void;
}

/**
 * Financial info section (monthly income, income proof).
 */
export const FinancialSection: React.FC<FinancialSectionProps> = ({ form, collapsed, onToggle }) => {
  const { t } = useTranslation('features/clients');

  return (
    <FormSection
      title={t('drawer.financial.title')}
      variant="quiet"
      divider
      collapsed={collapsed}
      onToggle={onToggle}
    >
      <Controller
        name="financial.monthlyIncome"
        control={form.control}
        render={({ field, fieldState }) => (
          <div className="flex flex-col">
            <Label style={labelStyle}>{t('drawer.financial.monthlyIncome')}</Label>
            <div className="relative">
              <Text component="span" style={currencyPrefixStyle}>R$</Text>
              <TextInput
                {...field}
                value={field.value ?? ''}
                placeholder="0"
                onChange={(e) => field.onChange(maskCurrency(e.target.value))}
                style={currencyInputPadding}
              />
            </div>
            <FieldError message={fieldState.error?.message} />
          </div>
        )}
      />

      <Controller
        name="financial.incomeProof"
        control={form.control}
        render={({ field }) => (
          <div className="flex flex-col">
            <Label style={labelStyle}>{t('drawer.financial.incomeProof')}</Label>
            <Select
              name={field.name}
              value={field.value ?? ''}
              onChange={(val) => field.onChange(val)}
              placeholder={t('drawer.financial.selectProof')}
              data={[
                { value: 'holerite', label: t('drawer.financial.proofTypes.holerite') },
                { value: 'irpf', label: t('drawer.financial.proofTypes.irpf') },
                { value: 'extrato', label: t('drawer.financial.proofTypes.extrato') },
                { value: 'pro_labore', label: t('drawer.financial.proofTypes.proLabore') },
                { value: 'outro', label: t('drawer.financial.proofTypes.outro') },
              ]}
            />
          </div>
        )}
      />
    </FormSection>
  );
};
